import React from 'react';
import { cn } from '@/lib/utils';
import tileUrl from '@/assets/psyforge-tile.svg';
import markUrl from '@/assets/psyforge-logo.png';

export type PsyforgeMarkVariant = 'tile' | 'mark' | 'mono';
export type PsyforgeMarkSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<PsyforgeMarkSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 96,
};

interface PsyforgeMarkProps {
  variant?: PsyforgeMarkVariant;
  size?: PsyforgeMarkSize;
  className?: string;
  /** Override pixel size (wins over `size` prop). */
  pixels?: number;
  /** Accessible label override. Defaults to "Psyforge". */
  label?: string;
}

/** Shared geometry so the colour and mono marks are the same drawing. */
const AnvilPath = () => (
  <>
    <path d="M112 330h228c14 0 26 11 26 25 0 8-4 15-11 20l-31 21c-8 5-13 14-13 24v6h30c9 0 16 7 16 16v10H155v-10c0-9 7-16 16-16h30v-6c0-10-5-19-13-24l-31-21c-7-5-11-12-11-20 0-14 12-25 26-25Z" />
    <path d="M340 330h44c12 0 22 10 22 22 0 12-10 22-22 22h-38l-6-44Z" />
  </>
);

const Sparks = () => (
  <>
    <path d="M108 178l14 22-14 22-14-22 14-22Z" />
    <path d="M406 200l11 17-11 17-11-17 11-17Z" />
    <path d="M370 108l9 14-9 14-9-14 9-14Z" />
  </>
);

/** Full-colour brand emblem — high-detail transparent artwork, no plate. */
const ColorMark: React.FC<{ px: number; label: string; className?: string }> = ({
  px,
  label,
  className,
}) => (
  <img
    src={markUrl}
    width={px}
    height={px}
    alt={label}
    style={{ width: px, height: px }}
    className={cn('inline-block shrink-0 select-none object-contain', className)}
    draggable={false}
  />
);

/** Monochrome vector emblem — inherits `currentColor`. */
const MonoMark: React.FC<{ px: number; label: string; className?: string }> = ({
  px,
  label,
  className,
}) => (
  <svg
    role="img"
    aria-label={label}
    width={px}
    height={px}
    viewBox="0 0 512 512"
    className={cn('inline-block shrink-0 select-none', className)}
    style={{ width: px, height: px }}
    fill="currentColor"
  >
    <AnvilPath />
    <path d="M256 92a104 104 0 1 1 0 208 104 104 0 0 1 0-208Zm0 26a78 78 0 1 0 0 156 78 78 0 0 0 0-156Z" />
    <path d="M248 122h16v148h-16Z" />
    <path d="M214 146c-20 8-31 28-25 47l-15 8c-16 12-19 38-4 53l12-11c-8-9-6-25 4-31l19-11-5-13c-4-12 3-25 16-30l-2-12Z" />
    <path d="M298 146c20 8 31 28 25 47l15 8c16 12 19 38 4 53l-12-11c8-9 6-25-4-31l-19-11 5-13c4-12-3-25-16-30l2-12Z" />
    <Sparks />
  </svg>
);

/**
 * Single brand-mark API for every Psyforge surface.
 *  - `mark`  → default: transparent vector emblem (header, sidebar, auth, splash)
 *  - `tile`  → self-contained dark rounded chip (favicon / OG / PDF cover only)
 *  - `mono`  → currentColor vector (PDF body, single-color contexts)
 */
export const PsyforgeMark: React.FC<PsyforgeMarkProps> = ({
  variant = 'mark',
  size = 'md',
  className,
  pixels,
  label = 'Psyforge logo mark',
}) => {
  const px = pixels ?? SIZE_PX[size];

  if (variant === 'mono') return <MonoMark px={px} label={label} className={className} />;
  if (variant === 'mark') return <ColorMark px={px} label={label} className={className} />;

  return (
    <img
      src={tileUrl}
      width={px}
      height={px}
      alt={label}
      // Explicit style sizing: a global `img { height: auto }` rule would
      // otherwise stretch the mark and make its glow bleed past the gutter.
      style={{ width: px, height: px }}
      className={cn('inline-block shrink-0 select-none object-contain', className)}
      draggable={false}
    />
  );
};

export default PsyforgeMark;
